import { useCallback, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import Button from "../components/Button"
import Icon from "../components/Icon"
import { COLOR, MONTHS, WEEKDAYS } from "../data/variables"
import { ICON } from "../components/Icon/data"
import { DaterangeValue } from "../components/Input/Daterange"

type UseCalendar = {
  date: string
  onChange: (key: string) => string
  clamping?: DaterangeValue[]
  rangeAnchor?: string
}

export const useCalendar = ({ date, onChange, clamping = [], rangeAnchor }: UseCalendar) => {
  const [currMonth, setCurrMonth] = useState<number>(DateTime.fromISO(date).month)
  const [currYear, setCurrYear] = useState<number>(DateTime.fromISO(date).year)
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  useEffect(() => {
    const luxonDate = DateTime.fromISO(date)
    const month = luxonDate.month
    const year = luxonDate.year
    setCurrMonth(month)
    setCurrYear(year)
  }, [date])

  const changeYear = useCallback((offset = 0) => {
    setCurrYear(currYear + offset)
  }, [currYear])

  const changeMonth = useCallback((offset = 0) => {
    const newMonth = currMonth + offset
    if (newMonth > 12) {
      setCurrMonth(1)
      changeYear(1)
    } else if (newMonth < 1) {
      setCurrMonth(12)
      changeYear(-1)
    } else {
      setCurrMonth(newMonth)
    }
  }, [currYear, currMonth, changeYear])

  const calendarHeader = useMemo(() => {
    const header = `${MONTHS[currMonth]} ${currYear}`
    return (
      <div className="Datepicker__month-header">
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CHEVRON_LEFT} color={COLOR.GRAY_60} />}
          onClick={() => changeMonth(-1)}
        />
        <div className="Datepicker__month-header__label">{header}</div>
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CHEVRON_RIGHT} color={COLOR.GRAY_60} />}
          onClick={() => changeMonth(1)}
        />
      </div>
    )
  }, [currMonth, currYear])

  const selectDate = useCallback((day: number) => {
    const newDate = DateTime.fromObject({ month: currMonth, day, year: currYear })
    onChange(newDate.toISODate())
  }, [currMonth, currYear, onChange])

  const isClamped = useCallback((day: number, month: number, year: number) => {
    const cellDate = DateTime.fromObject({ day, month, year }).startOf('day')
    return clamping.some(({ from, to }) => {
      const fromDate = from ? DateTime.fromISO(from).startOf('day') : null
      const toDate = to ? DateTime.fromISO(to).startOf('day') : null
      if (fromDate && toDate) return cellDate >= fromDate && cellDate <= toDate
      if (fromDate) return cellDate >= fromDate
      if (toDate) return cellDate <= toDate
      return false
    })
  }, [clamping])

  const calendarDates = useMemo(() => {
    const luxonDate = DateTime.fromObject({ month: currMonth, year: currYear })
    const daysInMonth = luxonDate.daysInMonth
    const startWeekday = luxonDate.startOf('month').weekday
    const todayMonth = DateTime.now().month
    const todayYear = DateTime.now().year
    const nowInMonth = todayMonth === luxonDate.month && todayYear === luxonDate.year
    const prevMonth = luxonDate.minus({ month: 1 })
    const nextMonth = luxonDate.plus({ month: 1 })

    const ownDate = DateTime.fromISO(date).startOf('day')
    const anchorDate = rangeAnchor && DateTime.fromISO(rangeAnchor).isValid
      ? DateTime.fromISO(rangeAnchor).startOf('day')
      : null
    const hoveredLuxon = hoveredDate ? DateTime.fromISO(hoveredDate).startOf('day') : null
    const movingDate = hoveredLuxon ?? ownDate
    const rangeLow = anchorDate ? DateTime.min(anchorDate, movingDate) : null
    const rangeHigh = anchorDate ? DateTime.max(anchorDate, movingDate) : null

    const buildCell = (day: number, month: number, year: number, preview: boolean, isToday: boolean) => {
      const cellDate = DateTime.fromObject({ day, month, year }).startOf('day')
      const clamped = isClamped(day, month, year)
      const isEndpoint = cellDate.equals(ownDate) || (anchorDate && cellDate.equals(anchorDate))
      const inRange = !isEndpoint && rangeLow && rangeHigh && cellDate > rangeLow && cellDate < rangeHigh
      return {
        day,
        month,
        year,
        preview,
        disabled: clamped,
        isToday,
        selected: isEndpoint,
        inRange
      }
    }

    const monthArr = []
    if (startWeekday !== 1) {
      const weekArr = []
      const lastMonthDays = prevMonth.daysInMonth
      let day = lastMonthDays - startWeekday + 2
      let preview = true
      let isToday = false
      Object.keys(WEEKDAYS).forEach(() => {
        if (day > lastMonthDays) {
          day = 1
          preview = false
        }

        if (nowInMonth && day === parseInt(DateTime.now().toFormat('D')) && !preview) {
          isToday = true
        }

        weekArr.push(buildCell(day, preview ? prevMonth.month : currMonth, preview ? prevMonth.year : currYear, preview, isToday))
        day = day + 1
        isToday = false
      })
      monthArr.push(weekArr)
    }

    const firstDay = monthArr.length ? monthArr[0][monthArr[0].length - 1].day + 1 : 1
    let numWeeks = Math.ceil((daysInMonth - firstDay + 1) / 7)
    let weekArr = []
    let day = firstDay
    let preview = false
    let isToday = false
    while (numWeeks !== 0) {
      Object.keys(WEEKDAYS).forEach(() => {
        if (day === daysInMonth + 1) {
          day = 1
          preview = true
        }

        if (nowInMonth && day === parseInt(DateTime.now().toFormat('D')) && !preview) {
          isToday = true
        }

        weekArr.push(buildCell(day, preview ? nextMonth.month : currMonth, preview ? nextMonth.year : currYear, preview, isToday))
        day = day + 1
        isToday = false
      })
      monthArr.push(weekArr)
      numWeeks = numWeeks - 1
      weekArr = []
    }

    return (
      <>
        {monthArr.map((week) => {
          return (
            <div className="Datepicker__month-wrapper__week">
              {week.map((d) => {
                const { disabled, preview, day, month, year, isToday, selected, inRange } = d
                const classArr = ['Datepicker__month-date']
                if (preview) classArr.push('preview')
                if (disabled) classArr.push('disabled')
                if (isToday) classArr.push('today')
                if (selected) classArr.push('selected')
                if (inRange) classArr.push('inrange')
                return (
                  <div
                    className={classArr.join(' ')}
                    role="button"
                    tabIndex={0}
                    onClick={() => (disabled || preview) ? {} : selectDate(day)}
                    onMouseEnter={() => (disabled || preview) ? {} : setHoveredDate(DateTime.fromObject({ day, month, year }).toISODate())}
                  >
                    {day}
                  </div>
                )
              })}
            </div>
          )
        })}
      </>
    )
  }, [date, currMonth, currYear, isClamped, rangeAnchor, hoveredDate])

  const getMonthCalendar = useMemo(() => {
    const luxonDate = DateTime.fromISO(date)
    if (luxonDate.isValid) {
      return (
        <div className="Datepicker__month">
          {calendarHeader}
          <div className="Datepicker__month-wrapper" onMouseLeave={() => setHoveredDate(null)}>
            <div className="Datepicker__month-wrapper__week">
              {Object.keys(WEEKDAYS).map((key) => {
                const weekday = WEEKDAYS[key]
                // only return the first letter of the weekday
                return (
                  <div className="Datepicker__month-date">
                    {weekday[0]}
                  </div>
                )
              })}
            </div>
            {calendarDates}
          </div>
        </div>
      )
    }

    return null
  }, [currMonth, currYear, date, calendarDates])

  return {
    getMonthCalendar
  }
}
